import type { UserRole } from '../user.types';

// vGrubs Announcements - replaces the deprecated whats-new feature.
// New announcement `type`s are added without changing the response shape, so
// the UI must switch on `type` and ignore unknown ones rather than render raw.

export type AnnouncementType = 'whatsNew';

export type AnnouncementApp = 'merchant' | 'admin' | 'sales' | 'tablet';

export type AnnouncementPlatform = 'ios' | 'android' | 'web';

// What a client (partner/sales/admin) sees via GET /v1/announcements/active.
// The backend already applies activity, publish/expire window, min version,
// (app, platform) targeting and per-user seen/dismissed state.
export type ActiveAnnouncement = {
  id: number;
  type: AnnouncementType;
  title: string;
  body: string; // markdown / rich text - sanitize before rendering
  /** Exactly one of `imageUrl` / `gifUrl` is set; draw `gifUrl ?? imageUrl`. */
  imageUrl: string | null;
  gifUrl: string | null;
  publishedAt: string | null;
  expiresAt: string | null;
  repeatInterval: number | null; // minutes; null = show once (never repeats)
  createdAt: string;
};

export type GetAnnouncementsHistoryParams = {
  page: number;
  limit: number;
};

// A single (app, platform) target as returned by the admin CRUD endpoints.
export type AnnouncementTarget = {
  app: AnnouncementApp;
  platform: AnnouncementPlatform;
  /** One of the two is set, the other null. */
  imageUrl: string | null;
  gifUrl: string | null;
  minAppVersion: string | null;
};

// Full announcement as returned by the admin list/detail endpoints.
// `seenCount` / `seenPreview` are read only: the PUT rejects them as unknown
// fields, which is why the wizard builds its payload field by field.
export type Announcement = {
  id: number;
  type: AnnouncementType;
  title: string;
  body: string;
  active: boolean;
  repeatInterval: number | null;
  publishedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  targets: AnnouncementTarget[];
  /** Distinct users who saw it, on any app and platform. */
  seenCount: number;
  /** Up to the 5 latest viewers, newest first. */
  seenPreview: AnnouncementViewerPreview[];
};

// --- Who saw it (admin app, roles admin/support) ---------------------------
// Nobody has a picture, so a person is drawn from their initials. `name` wins
// when set; see `viewerName`.

export type AnnouncementPerson = {
  userId: number;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  role: UserRole;
};

export type AnnouncementViewerPreview = AnnouncementPerson & {
  lastSeenAt: string;
};

// One app + platform a viewer saw it on. A row with `viewCount: 0` and null
// dates is a "Don't show again" clicked without ever looking at it.
export type AnnouncementViewerSeenOn = {
  app: AnnouncementApp;
  platform: AnnouncementPlatform;
  firstSeenAt: string | null;
  seenAt: string | null;
  viewCount: number;
  dontShowAgain: boolean;
};

// One row per user, newest `lastSeenAt` first. With an `app` / `platform`
// filter, `seenOn` and the totals cover only the matching pairs.
export type AnnouncementViewer = AnnouncementPerson & {
  email: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  viewCount: number;
  /** Dismissed for good on at least one app + platform. */
  dontShowAgain: boolean;
  seenOn: AnnouncementViewerSeenOn[];
};

export type GetAnnouncementViewersParams = {
  page: number;
  limit: number;
  app?: AnnouncementApp;
  platform?: AnnouncementPlatform;
  role?: UserRole;
  query?: string;
};

// Exactly `days` rows, oldest first, zeros included. Only counted since the
// feature shipped, so an empty chart next to `seenCount > 0` is expected.
export type AnnouncementViewsPerDay = {
  date: string; // 'YYYY-MM-DD' in the requested zone
  views: number;
  uniqueViewers: number;
  dismissals: number;
};

export type GetAnnouncementViewsPerDayParams = {
  days?: number; // 1-90, default 30
  timezone?: string; // IANA, default America/New_York
};

export type AnnouncementActivity = AnnouncementPerson & {
  id: number;
  /** `dismissed` is "Don't show again". */
  action: 'seen' | 'dismissed';
  app: AnnouncementApp;
  platform: AnnouncementPlatform;
  createdAt: string;
};

// Target as sent when creating/updating (no URLs; the image or GIF is a file
// part). On update a target sent without a file keeps what is stored.
export type AnnouncementTargetInput = {
  app: AnnouncementApp;
  platform: AnnouncementPlatform;
  minAppVersion?: string | null;
};

// The JSON `announcement` part of the create/update multipart body.
export type AnnouncementPayload = {
  type: AnnouncementType;
  title: string;
  body: string;
  active?: boolean;
  repeatInterval?: number | null;
  publishedAt?: string | null;
  expiresAt?: string | null;
  // Create: required (at least one). Update: omit to leave targeting untouched;
  // when sent it fully replaces the target set.
  targets?: AnnouncementTargetInput[];
};

export type GetAnnouncementsParams = {
  page: number;
  limit: number;
  type?: AnnouncementType;
  app?: AnnouncementApp;
  platform?: AnnouncementPlatform;
  active?: boolean;
  query?: string;
};

// The list of (app, platform) combinations the UI offers as targets.
export const ANNOUNCEMENT_APPS: AnnouncementApp[] = [
  'merchant',
  'admin',
  'sales',
  'tablet',
];

export const ANNOUNCEMENT_PLATFORMS: AnnouncementPlatform[] = [
  'ios',
  'android',
  'web',
];

// Multipart file field name for a target's image, e.g. `image_merchant_ios`.
export const announcementImageField = (
  app: AnnouncementApp,
  platform: AnnouncementPlatform
) => `image_${app}_${platform}`;

// Multipart file field name for a target's GIF, e.g. `gif_tablet_android`.
// A target sends this or `announcementImageField`, never both.
export const announcementGifField = (
  app: AnnouncementApp,
  platform: AnnouncementPlatform
) => `gif_${app}_${platform}`;
